import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDashboardMain } from '@/api/dashboard'

export const useDashboardStore = defineStore('dashboard', () => {
  // State
  const statistics = ref({
    totalCustomers: 0,
    totalTeachers: 0,
    totalHorses: 0,
    todayBookings: 0,
    monthlyIncome: 0,
    totalVipPackageCount: 0,
    activeVipPackageCount: 0,
    totalVipSubscriptionCount: 0,
    activeVipSubscriptionCount: 0,
    totalRemainingVipHours: 0,
    vipRevenue: 0
  })
  
  const recentBookings = ref([])
  const weeklyData = ref([])
  const loading = ref(false)

  // Actions
  const fetchDashboardData = async (forceRefresh = false) => {
    // Only use cached data if not forcing refresh and we have valid data
    if (!forceRefresh && statistics.value.totalCustomers > 0) {
      console.log('Using cached dashboard data')
      return { success: true, data: statistics.value }
    }
    
    // Skip if already loading
    if (loading.value) {
      return { success: false, message: 'Already loading' }
    }
    
    loading.value = true
    
    try {
      console.log('Fetching dashboard data from API...')
      const res = await getDashboardMain()
      
      console.log('Dashboard API response:', res)
      
      if (res.success) {
        // Map API response fields to store fields
        statistics.value = {
          totalCustomers: res.data?.customerCount || 0,
          totalTeachers: res.data?.teacherCount || 0,
          totalHorses: res.data?.horseCount || 0,
          todayBookings: res.data?.bookingCount || 0,
          monthlyIncome: res.data?.income || 0,
          totalVipPackageCount: res.data?.totalVipPackageCount || 0,
          activeVipPackageCount: res.data?.activeVipPackageCount || 0,
          totalVipSubscriptionCount: res.data?.totalVipSubscriptionCount || 0,
          activeVipSubscriptionCount: res.data?.activeVipSubscriptionCount || 0,
          totalRemainingVipHours: res.data?.totalRemainingVipHours || 0,
          vipRevenue: res.data?.vipRevenue || 0
        }
        
        console.log('Dashboard statistics:', statistics.value)
        
        // Transform recent bookings from the new API structure
        const bookingsList = res.data?.list || []
        console.log('Raw bookings list from API:', bookingsList)
        
        // Calculate total booked hours for today
        let todayBookingHours = 0
        bookingsList.forEach(booking => {
          booking.bookingTimes?.forEach(bookingTime => {
            todayBookingHours += bookingTime?.bookedHours?.length || 0
          })
        })
        
        // Update todayBookings to be the total hours count
        statistics.value.todayBookings = todayBookingHours
        
        recentBookings.value = bookingsList.map(booking => {
          // Extract the first booking time for display
          const firstBookingTime = booking.bookingTimes?.[0]
          
          // Collect all booked hours for this booking time
          const allHours = firstBookingTime?.bookedHours 
            ? firstBookingTime.bookedHours
                .map(h => h.hour)
                .sort((a, b) => a - b)
            : []
          
          // Format date
          let dateStr = ''
          let timeStr = ''
          if (firstBookingTime) {
            const bookingDate = new Date(firstBookingTime.bookingDate)
            const year = bookingDate.getFullYear()
            const month = bookingDate.getMonth() + 1
            const day = bookingDate.getDate()
            dateStr = `${year}/${month}/${day}`
            
            // Format multiple hours as "10:00, 12:00"
            if (allHours.length > 0) {
              timeStr = allHours.map(h => `${String(h).padStart(2, '0')}:00`).join(', ')
            }
          }
          
          return {
            id: booking.id,
            date: dateStr,
            time: timeStr,
            teacherName: booking.name || '-',
            customerName: booking.customerName || '-',
            customerId: booking.customerId || booking.customerVipId || booking.customer?.id || null,
            status: booking.status || 'confirmed',
            orderNumber: booking.orderNumber,
            createdAt: booking.createdAt
          }
        })
        
        console.log('Transformed recent bookings:', recentBookings.value)
        
        weeklyData.value = res.data?.weeklyBookings || []
        
        return { success: true, data: res.data }
      }
      
      return { success: false, message: res.message }
    } catch (error) {
      console.error('Dashboard API error:', error)
      // Return cached data on error if available
      if (statistics.value.totalCustomers > 0) {
        return { success: true, data: statistics.value, fromCache: true }
      }
      return { success: false, error: error.message }
    } finally {
      loading.value = false
    }
  }

  return {
    // State
    statistics,
    recentBookings,
    weeklyData,
    loading,
    
    // Actions
    fetchDashboardData
  }
})